import React from "react";
import {
  Row,
  Col,
  Form,
  FormGroup,
  Label,
  Input,
  Card,
  CardBody,
} from "reactstrap";
import Select from "react-select";
import { SettingsProps, TaxSettingsValues } from "../../types/settings";
import SettingsButtons from "./SettingsButtons";
import { Formik } from "formik";
import { taxSettingsValidation } from "rules/settings";

const TaxSettings: React.FC<SettingsProps> = ({ 
  onGlobalCancel, 
  isSaving, 
  onSave 
}) => {
  const initialValues: TaxSettingsValues = {
    taxCalculationMethod: { value: 'per_item', label: 'Per Item' },
    defaultTaxClass: { value: 'standard', label: 'Standard Rate' },
    standardRate: "",
    reducedRate: "",
    pricesIncludeTax: false,
    displayPricesInclTax: false,
    calculateDuty: false,
    defaultDutyRate: '',
    taxZones: [],
    taxExemptCategories: []
  };

  const handleSave = async (values: TaxSettingsValues) => {
    console.log('Submitting tax settings:', values);
    await onSave(async () => {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));
      // TODO: Add actual API call here
    });
  };

  return (
    <div className="bg-transparent">
      <h5 className="mb-4">Tax & Duty Settings</h5>
      <Formik
        initialValues={initialValues}
        validationSchema={taxSettingsValidation}
        onSubmit={handleSave}
        validateOnMount={true}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
          handleSubmit,
        }) => (
          <Form>
            <Card className="mb-4">
              <CardBody>
                <h6 className="mb-3">Tax Calculation</h6>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="taxCalculationMethod">Tax Calculation Method</Label>
                      <Select
                        id="taxCalculationMethod"
                        name="taxCalculationMethod"
                        value={values.taxCalculationMethod}
                        onChange={(option) => setFieldValue("taxCalculationMethod", option)}
                        options={[
                          { value: 'per_item', label: 'Per Item' },
                          { value: 'subtotal', label: 'Based on Subtotal' },
                          { value: 'total', label: 'Based on Total' },
                        ]}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="defaultTaxClass">Default Tax Class</Label>
                      <Select
                        id="defaultTaxClass"
                        name="defaultTaxClass"
                        value={values.defaultTaxClass}
                        onChange={(option) => setFieldValue("defaultTaxClass", option)}
                        options={[
                          { value: 'standard', label: 'Standard Rate' },
                          { value: 'reduced', label: 'Reduced Rate' },
                          { value: 'zero', label: 'Zero Rate' },
                        ]}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <hr className="my-4" />

                <h6 className="mb-3">Tax Rates</h6>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="standardRate">Standard Tax Rate (%)</Label>
                      <Input
                        type="number"
                        id="standardRate"
                        name="standardRate"
                        value={values.standardRate}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Enter standard tax rate"
                        min="0"
                        max="100"
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="reducedRate">Reduced Tax Rate (%)</Label>
                      <Input
                        type="number"
                        id="reducedRate"
                        name="reducedRate"
                        value={values.reducedRate}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Enter reduced tax rate"
                        min="0"
                        max="100"
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <FormGroup check className="mb-3">
                      <Input 
                        type="checkbox" 
                        id="pricesIncludeTax"
                        name="pricesIncludeTax"
                        checked={values.pricesIncludeTax}
                        onChange={handleChange}
                      />
                      <Label check for="pricesIncludeTax">
                        Prices Include Tax
                      </Label>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup check className="mb-3">
                      <Input 
                        type="checkbox" 
                        id="displayPricesInclTax"
                        name="displayPricesInclTax"
                        checked={values.displayPricesInclTax}
                        onChange={handleChange}
                      />
                      <Label check for="displayPricesInclTax">
                        Display Prices Including Tax
                      </Label>
                    </FormGroup>
                  </Col>
                </Row>

                <hr className="my-4" />

                <h6 className="mb-3">Import Duties</h6>
                <Row>
                  <Col md={6}>
                    <FormGroup check className="mb-3">
                      <Input 
                        type="checkbox" 
                        id="calculateDuty"
                        name="calculateDuty"
                        checked={values.calculateDuty}
                        onChange={handleChange}
                      />
                      <Label check for="calculateDuty">
                        Calculate Import Duty
                      </Label>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="defaultDutyRate">Default Duty Rate (%)</Label>
                      <Input
                        type="number"
                        id="defaultDutyRate"
                        name="defaultDutyRate"
                        value={values.defaultDutyRate}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Enter default duty rate"
                        min="0"
                        max="100"
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <hr className="my-4" />

                <h6 className="mb-3">Tax Zones</h6>
                <Row>
                  <Col md={12}>
                    <FormGroup>
                      <Label for="taxZones">Manage Tax Zones</Label>
                      <Select
                        isMulti
                        id="taxZones"
                        name="taxZones"
                        value={values.taxZones}
                        onChange={(options) => setFieldValue("taxZones", options)}
                        options={[
                          { value: 'ghana', label: 'Ghana' },
                          { value: 'nigeria', label: 'Nigeria' },
                          { value: 'kenya', label: 'Kenya' },
                          { value: 'other', label: 'Other Countries' },
                        ]}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md={12}>
                    <FormGroup>
                      <Label for="taxExemptCategories">Tax-Exempt Categories</Label>
                      <Select
                        isMulti
                        id="taxExemptCategories"
                        name="taxExemptCategories"
                        value={values.taxExemptCategories}
                        onChange={(options) => setFieldValue("taxExemptCategories", options)}
                        options={[
                          { value: 'food', label: 'Food & Beverages' },
                          { value: 'medical', label: 'Medical Supplies' },
                          { value: 'books', label: 'Books & Education' },
                        ]}
                      />
                    </FormGroup>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            <SettingsButtons
              onSave={handleSubmit}
              onCancel={onGlobalCancel}
              saveDisabled={Object.keys(errors).length > 0}
              isSaving={isSaving}
            />
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default TaxSettings; 